// Centralized status → chip mapping
import { ChipColor } from '../Table/Table.hooks';
import {
  INV_AVAILABILITY_BLOCKED_LABEL,
  INV_AVAILABILITY_IN_REQUEST_LABEL,
  INV_AVAILABILITY_WRITE_OFF_LABEL,
  INV_STATUS_BRANCH_LABEL,
  INV_STATUS_TRANSIT_LABEL,
  INV_STATUS_WAREHOUSE_LABEL,
} from '../../../constants/inventory-status-labels';

export type StatusChip = { label: string; color: ChipColor };
type StatusMap = Record<string, StatusChip>;

// Helpers
const normalize = (status: string) => status.trim().toLowerCase();
const fallback = (status: string): StatusChip => ({
  label: status,
  color: 'default',
});

// Orders
const orderStatusMap: StatusMap = {
  borrador: { label: 'Borrador', color: 'default' },
  draft: { label: 'Borrador', color: 'default' },
  confirmada: { label: 'Confirmada', color: 'info' },
  confirmed: { label: 'Confirmada', color: 'info' },
  'pago parcial': { label: 'Pago Parcial', color: 'warning' },
  partial_paid: { label: 'Pago Parcial', color: 'warning' },
  'partial paid': { label: 'Pago Parcial', color: 'warning' },
  'partial-paid': { label: 'Pago Parcial', color: 'warning' },
  pagada: { label: 'Pagada', color: 'success' },
  paid: { label: 'Pagada', color: 'success' },
  entregada: { label: 'Entregada', color: 'success' },
  delivered: { label: 'Entregada', color: 'success' },
  cancelada: { label: 'Cancelada', color: 'error' },
  cancelled: { label: 'Cancelada', color: 'error' },
  canceled: { label: 'Cancelada', color: 'error' },
  reembolsada: { label: 'Reembolsada', color: 'error' },
  refunded: { label: 'Reembolsada', color: 'error' },
};

export const mapOrderStatus = (status: string): StatusChip => {
  if (!status) return fallback('—');
  const key = normalize(status);
  return orderStatusMap[key] || fallback(status);
};

// Order fulfillment (mixed-branch logistics), independent of payment status above.
// NOT_REQUIRED is intentionally absent — callers should skip rendering the chip in that case.
// Labels kept in sync with utils/sales/saleFulfillmentPresentation (single source of truth).
const orderFulfillmentStatusMap: StatusMap = {
  pending: { label: 'Envío pendiente', color: 'warning' },
  completed: { label: 'Envío completado', color: 'success' },
  ready: { label: 'Envío completado', color: 'success' },
  failed: { label: 'Rechazada', color: 'error' },
  cancelled: { label: 'Envío cancelado', color: 'error' },
  canceled: { label: 'Envío cancelado', color: 'error' },
};

export const mapOrderFulfillmentStatus = (status: string): StatusChip => {
  if (!status || status === 'NOT_REQUIRED') return fallback('—');
  const key = normalize(status);
  return orderFulfillmentStatusMap[key] || fallback(status);
};

// Customer handoff delivery (independent of payment / logistics)
const orderDeliveryStatusMap: StatusMap = {
  none: { label: 'Pendiente de entrega', color: 'warning' },
  partial: { label: 'Entrega parcial', color: 'info' },
  full: { label: 'Entrega completa', color: 'success' },
  cancelled: { label: 'Entrega cancelada', color: 'error' },
  canceled: { label: 'Entrega cancelada', color: 'error' },
};

export const mapOrderDeliveryStatus = (status: string): StatusChip => {
  if (!status) return fallback('—');
  const key = normalize(status);
  return orderDeliveryStatusMap[key] || fallback(status);
};

// Transfers
const transferStatusMap: StatusMap = {
  pendiente: { label: 'Pendiente', color: 'warning' },
  completado: { label: 'Recibido', color: 'success' },
  cancelado: { label: 'Cancelado', color: 'error' },
};

export const mapTransferStatus = (status: string): StatusChip => {
  if (!status) return fallback('—');
  const key = normalize(status);
  return transferStatusMap[key] || fallback(status);
};

const requestStatusMap: StatusMap = {
  pending: { label: 'Pendiente', color: 'warning' },
  pendiente: { label: 'Pendiente', color: 'warning' },
  approved: { label: 'Aprobada', color: 'success' },
  aprobada: { label: 'Aprobada', color: 'success' },
  rejected: { label: 'Rechazada', color: 'error' },
  rechazada: { label: 'Rechazada', color: 'error' },
};

export const mapRequestStatus = (status: string): StatusChip => {
  if (!status) return fallback('—');
  const key = normalize(status);
  return requestStatusMap[key] || fallback(status);
};

const requestItemStatusMap: StatusMap = {
  pending: { label: 'Pendiente', color: 'warning' },
  pendiente: { label: 'Pendiente', color: 'warning' },
  approved: { label: 'Aprobada', color: 'success' },
  aprobada: { label: 'Aprobada', color: 'success' },
  rejected: { label: 'Rechazada', color: 'error' },
  rechazada: { label: 'Rechazada', color: 'error' },
};

export const mapRequestItemStatus = (status: string): StatusChip => {
  if (!status) return fallback('—');
  const key = normalize(status);
  return requestItemStatusMap[key] || fallback(status);
};

// Products
const productStatusMap: StatusMap = {
  activo: { label: 'Activo', color: 'success' },
  inactivo: { label: 'Inactivo', color: 'error' },
};

export const mapProductStatus = (status: string): StatusChip => {
  if (!status) return fallback('—');
  const key = normalize(status);
  return productStatusMap[key] || fallback(status);
};

// Inventory (records)
const inventoryRecordStatusMap: StatusMap = {
  activo: { label: 'Activo', color: 'success' },
  active: { label: 'Activo', color: 'success' },
  completado: { label: 'Completado', color: 'success' },
  completed: { label: 'Completado', color: 'success' },
  pendiente: { label: 'Pendiente', color: 'warning' },
  pending: { label: 'Pendiente', color: 'warning' },
  cancelado: { label: 'Cancelado', color: 'error' },
  cancelled: { label: 'Cancelado', color: 'error' },
  canceled: { label: 'Cancelado', color: 'error' },
  borrador: { label: 'Borrador', color: 'default' },
  draft: { label: 'Borrador', color: 'default' },
};

export const mapInventoryRecordStatus = (status: string): StatusChip => {
  if (!status) return fallback('—');
  const key = normalize(status);
  return inventoryRecordStatusMap[key] || fallback(status);
};

// Inventory availability (branch/warehouse items)
const inventoryAvailabilityMap: StatusMap = {
  disponible: { label: 'Disponible', color: 'success' },
  vendido: { label: 'Vendido', color: 'info' },
  reservado: { label: INV_AVAILABILITY_BLOCKED_LABEL, color: 'warning' },
  bloqueada: { label: INV_AVAILABILITY_BLOCKED_LABEL, color: 'warning' },
  'pago pendiente': { label: 'Pago pendiente', color: 'error' },
  'pago parcial': { label: 'Pago parcial', color: 'warning' },
  cancelado: { label: INV_AVAILABILITY_WRITE_OFF_LABEL, color: 'default' },
  baja: { label: INV_AVAILABILITY_WRITE_OFF_LABEL, color: 'default' },
  cancelled: { label: INV_AVAILABILITY_WRITE_OFF_LABEL, color: 'default' },
  canceled: { label: INV_AVAILABILITY_WRITE_OFF_LABEL, color: 'default' },
  asignado: { label: INV_STATUS_BRANCH_LABEL, color: 'success' },
  'en sucursal': { label: INV_STATUS_BRANCH_LABEL, color: 'success' },
  'en transito': { label: 'En Tránsito', color: 'info' },
  'en tránsito': { label: 'En Tránsito', color: 'info' },
  'en solicitud': { label: INV_AVAILABILITY_IN_REQUEST_LABEL, color: 'info' },
};

export const mapInventoryAvailability = (status: string): StatusChip => {
  if (!status) return fallback('—');
  const key = normalize(status);
  return inventoryAvailabilityMap[key] || fallback(status);
};

// Warehouse item status (almacén)
const warehouseStatusMap: StatusMap = {
  asignado: { label: INV_STATUS_BRANCH_LABEL, color: 'success' },
  'en sucursal': { label: INV_STATUS_BRANCH_LABEL, color: 'success' },
  'en transito': { label: 'En Tránsito', color: 'info' },
  'en tránsito': { label: 'En Tránsito', color: 'info' },
  'sin asignar': { label: INV_STATUS_WAREHOUSE_LABEL, color: 'default' },
  'en almacén': { label: INV_STATUS_WAREHOUSE_LABEL, color: 'default' },
};

export const mapWarehouseStatus = (status: string): StatusChip => {
  if (!status) return fallback('—');
  const key = normalize(status);
  return warehouseStatusMap[key] || fallback(status);
};

// Sale fulfillment group (per source-location logistics leg within a mixed sale)
// Labels kept in sync with utils/sales/saleFulfillmentPresentation (single source of truth).
const saleFulfillmentGroupStatusMap: StatusMap = {
  pending_sol: { label: INV_AVAILABILITY_IN_REQUEST_LABEL, color: 'warning' },
  awaiting_source: { label: INV_AVAILABILITY_IN_REQUEST_LABEL, color: 'warning' },
  in_transit: { label: INV_STATUS_TRANSIT_LABEL, color: 'info' },
  received: { label: INV_STATUS_BRANCH_LABEL, color: 'success' },
  failed: { label: 'Rechazada', color: 'error' },
};

export const mapSaleFulfillmentGroupStatus = (status: string): StatusChip => {
  if (!status) return fallback('—');
  const key = normalize(status);
  return saleFulfillmentGroupStatusMap[key] || fallback(status);
};

// Generic mapper
export const mapStatusGeneric = (status: string, map: StatusMap): StatusChip => {
  if (!status) return fallback('—');
  const key = normalize(status);
  return map[key] || fallback(status);
};
